import { useMutation, useQuery, UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { throwIfCutShort, useFetchAllRows, UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import { SelectOptionType } from '@/components/content/SelectBoxTypes'
import { getShipperListV2 } from '@/services/shipper/apis'
import { confirmErpInbounds, deleteErpInbounds, getErpInbounds, saveErpInbounds } from './apis'
import { ERP_INBOUNDS_FETCH_SIZE } from './consts'
import {
  ConfirmErpInboundsReq,
  DeleteErpInboundsReq,
  DeleteErpInboundsRes,
  ErpInboundItem,
  ErpInboundSaveResultItem,
  GetErpInboundsReq,
  SaveErpInboundsReq,
} from './types'

/**
 * 쿼리 키 앞머리에 이 화면의 메뉴코드를 둔다.
 *
 * `MenuOnSessionWrap.remove()` 가 탭을 닫을 때 키에 메뉴코드가 든 쿼리만 캐시에서 지운다
 * ("탭 삭제 시 관련 쿼리 데이터도 삭제, 사용 시 쿼리 키에 메뉴 코드가 포함되어야 함").
 * 빠져 있으면 탭을 닫아도 목록 캐시가 남는다.
 */
const MENU_CODE = 'egr06'

const PATH = [MENU_CODE, 'INBOUND', 'ERP_INBOUND'] as const

/** 조회 조건 — BE 구간(page·size)은 공용 훅이 채운다. 키에 넣지 않아 페이지를 넘겨도 다시 받지 않는다. */
export type ErpInboundsCondition = Omit<GetErpInboundsReq, 'page' | 'size'>

export const erpInboundQueryKeys = {
  all: PATH,
  list: (condition: ErpInboundsCondition | null) => [...PATH, 'LIST', condition] as const,
  shippers: [...PATH, 'SHIPPERS'] as const,
}

/**
 * 구간 경계에서 겹친 행을 거르는 키 — ERP 행(id)에 ArrivalNotice 가 둘 이상 조인되면 같은 id 가 여러 행으로 온다.
 * id 만으로 거르면 그 행이 사라지므로 조인된 AN PK(anId)까지 붙인다(BE 정렬 보정 LOGISPRD-15337 에서 펼침 여부 실측).
 * 화면이 편집분을 받은 행에 덮을 때도 같은 키를 쓴다.
 */
export const erpRowKey = (row: ErpInboundItem): string => `${row.id}|${row.anId ?? ''}`

/**
 * 일반입고(ERP) 목록 — 전량 조회(LOGISPRD-15268). BE 상한(1,000)씩 끊어 받고 화면은 10,000건씩 나눈다.
 *
 * `condition` 이 없으면 조회하지 않는다 — 최초 진입에서는 조회하지 않는다(1.0 EGR01 도 진입 시 그리드가 비어 있다).
 * 다른 메뉴를 다녀와 URL 에 조회 조건이 복원된 경우에는 화면이 그 조건으로 켠다 — 캐시가 있으면 다시 받지 않는다(결정 12).
 * 실패 안내는 공용 훅의 전역 알럿(서버 meta.message) 하나다 — 화면이 따로 띄우지 않는다.
 */
export const useGetErpInbounds = (condition: ErpInboundsCondition | null): UseFetchAllRowsResult<ErpInboundItem> =>
  useFetchAllRows({
    queryKey: erpInboundQueryKeys.list(condition),
    fetchPage: async (page, size = ERP_INBOUNDS_FETCH_SIZE) => {
      // enabled 가 condition !== null 이라 null 로는 불리지 않는다(단언 대신 spread — null 이면 조건 없는 요청이 아니라 훅이 끈다)
      const res = await getErpInbounds({ ...condition, page, size })
      // pageInfo 가 빠진 응답을 0건으로 메우지 않는다 — 없으면 여기서 던져 실패 알럿으로 드러낸다(rgr13 선례).
      const { items, pageInfo } = res.data
      throwIfCutShort(page, size, items.length, pageInfo.totalCount)
      return { items, totalCount: pageInfo.totalCount }
    },
    chunkSize: ERP_INBOUNDS_FETCH_SIZE,
    getKey: erpRowKey,
    enabled: condition !== null,
  })

/**
 * 화주명 셀렉트 옵션 — `POST /api/v1/get-shippers` 전체 목록.
 *
 * 조회 필터의 `shipperCode` 는 ArrivalNotice.SPR_NM EQUAL 이라 value 는 화주코드, label 은 화주명이다.
 * 표의 화주코드 컬럼도 같은 목록으로 코드를 명으로 바꿔 보여 준다(ST11 과 같은 배선).
 */
export const useGetErpInboundShipperOptions = (): UseQueryResult<SelectOptionType<string>[], Error> =>
  useQuery({
    queryKey: erpInboundQueryKeys.shippers,
    meta: { errorToast: true }, // 실패는 옛 알럿 대신 새 디자인 알럿(queryClient.ts — 옵션 이름 errorToast 는 옛 이름 그대로)
    queryFn: async (): Promise<SelectOptionType<string>[]> => {
      const res = await getShipperListV2({ shipperNames: [], logisticsServiceTypes: [], page: 0, size: 10000 })
      return (res?.data?.items ?? []).map((s) => ({ value: s.shipperCode, label: s.shipperName || s.shipperCode }))
    },
    staleTime: API_COMMON_CACHE_TIME,
  })

/** 선택 행 삭제. 사유(REMARK)는 호출부가 삭제 확인 모달에서 받아 채운다. */
export const useDeleteErpInbounds = (): UseMutationResult<DeleteErpInboundsRes, Error, DeleteErpInboundsReq> =>
  useMutation({
    mutationFn: async (params: DeleteErpInboundsReq): Promise<DeleteErpInboundsRes> => {
      const res = await deleteErpInbounds(params)
      return res?.data ?? { successCount: 0 }
    },
  })

/** 선택 행 입고확정. 확정된 입고예정 id 목록을 돌려준다. */
export const useConfirmErpInbounds = (): UseMutationResult<number[], Error, ConfirmErpInboundsReq> =>
  useMutation({
    mutationFn: async (params: ConfirmErpInboundsReq): Promise<number[]> => {
      const res = await confirmErpInbounds(params)
      return res?.data?.items ?? []
    },
  })

/**
 * 선택 행 저장. 행 단위 결과를 그대로 돌려준다 — 성공 판정(`MSGTYP === 'S'`)은 화면이 한다.
 * BE 가 요청 처리 중 예외를 던져도 HTTP 는 200 이고 `T_RESULT` 행마다 실패 코드가 실려 온다
 * (`ErpInboundController.createFailureResponse`) — axios 실패(catch)만 보면 이 실패를 놓친다.
 */
export const useSaveErpInbounds = (): UseMutationResult<ErpInboundSaveResultItem[], Error, SaveErpInboundsReq> =>
  useMutation({
    mutationFn: async (params: SaveErpInboundsReq): Promise<ErpInboundSaveResultItem[]> => {
      const res = await saveErpInbounds(params)
      return res?.data?.T_RESULT ?? []
    },
  })
